import { db } from "@/lib/firebase";
import {
  ActionOut,
  MemberOut,
  OrganizationOut,
  OrganizationService,
  OrgRole,
} from "@/lib/organizationService";
import {
  collection,
  type DocumentData,
  limit as fbLimit,
  onSnapshot,
  orderBy,
  query,
  type QueryDocumentSnapshot,
  type Timestamp,
  where,
} from "firebase/firestore";
import { StateCreator } from "zustand";
import { StoreState } from "..";

/** A ledger entry plus its document id, for React keys. */
export interface ActionEntry extends ActionOut {
  id: string;
}

export interface OrgSlice {
  // State
  orgId: string | null;
  orgName: string | null;
  orgRole: OrgRole | null;
  isLoadingOrg: boolean;
  hasCheckedOrg: boolean;
  orgFetchError: string | null;
  members: MemberOut[];
  isLoadingMembers: boolean;
  membersError: string | null;
  actions: ActionEntry[];
  isLoadingActions: boolean;
  actionsError: string | null;

  // Actions
  fetchMyOrganization: () => Promise<void>;
  createOrganization: (name: string) => Promise<void>;
  clearOrganization: () => void;
  getMemberDisplayName: (uid: string) => string;
  subscribeToMembers: (orgId: string) => () => void;
  subscribeToActions: (
    orgId: string,
    options?: { targetPath?: string; limit?: number },
  ) => () => void;

  /* internals */
  _membersSubscription: { key: string; stop: () => void } | null;
  _actionsSubscription: { key: string; stop: () => void } | null;
}

function toMember(doc: QueryDocumentSnapshot<DocumentData>): MemberOut {
  const data = doc.data();
  const joinedAt = data.joined_at as Timestamp | null;
  return {
    uid: doc.id,
    role: data.role,
    joined_at: (joinedAt?.toDate() ?? new Date()).toISOString(),
    email: data.email ?? null,
    display_name: data.display_name ?? null,
    invited_by: data.invited_by ?? null,
  };
}

/**
 * Mapped by hand rather than through `UserActionsLedgerSchema`: its `action` is
 * a closed enum, so a name a newer backend emits would drop the entry from the
 * feed entirely, where `describeAction` already falls back to showing the raw
 * name. Under-labelling beats disappearing.
 */
function toActionEntry(doc: QueryDocumentSnapshot<DocumentData>): ActionEntry {
  const data = doc.data();
  const at = data.at as Timestamp | null;
  return {
    id: doc.id,
    // Null while a local write waits for the server clock — it just happened.
    at: (at?.toDate() ?? new Date()).toISOString(),
    actor_uid: data.actor_uid ?? null,
    actor_type: data.actor_type,
    on_behalf_of: data.on_behalf_of ?? null,
    action: data.action,
    target_path: data.target_path,
    domain_id: data.domain_id ?? null,
    summary: data.summary ?? null,
    source_thread_id: data.source_thread_id ?? null,
    source_message_id: data.source_message_id ?? null,
  };
}

export const createOrgSlice: StateCreator<StoreState, [], [], OrgSlice> = (
  set,
  get,
) => ({
  orgId: null,
  orgName: null,
  orgRole: null,
  isLoadingOrg: false,
  hasCheckedOrg: false,
  orgFetchError: null,
  members: [],
  isLoadingMembers: true,
  membersError: null,
  actions: [],
  isLoadingActions: true,
  actionsError: null,
  _membersSubscription: null,
  _actionsSubscription: null,

  fetchMyOrganization: async () => {
    set({ isLoadingOrg: true, orgFetchError: null });
    try {
      const org: OrganizationOut | null =
        await OrganizationService.getMyOrganization();
      set({
        orgId: org?.id ?? null,
        orgName: org?.name ?? null,
        orgRole: org?.role ?? null,
        isLoadingOrg: false,
        hasCheckedOrg: true,
        orgFetchError: null,
      });
    } catch (error) {
      // A request failure is NOT the same as a confirmed "no org" (404,
      // which the service resolves to `null` rather than throwing). Leave
      // hasCheckedOrg false so the guard shows a retry state instead of
      // routing an existing org member into org creation because their
      // /me lookup happened to fail.
      console.error("Error fetching organization:", error);
      set({
        isLoadingOrg: false,
        orgFetchError:
          error instanceof Error
            ? error.message
            : "Failed to fetch organization",
      });
    }
  },

  createOrganization: async (name: string) => {
    const org = await OrganizationService.createOrganization(name);
    set({ orgId: org.id, orgName: org.name, orgRole: org.role });
  },

  clearOrganization: () => {
    get()._membersSubscription?.stop();
    get()._actionsSubscription?.stop();
    set({
      orgId: null,
      orgName: null,
      orgRole: null,
      isLoadingOrg: false,
      hasCheckedOrg: false,
      orgFetchError: null,
      members: [],
      isLoadingMembers: true,
      membersError: null,
      actions: [],
      isLoadingActions: true,
      actionsError: null,
    });
  },

  /**
   * Live membership roster. Backend-only writes (`firestore.rules`), so this is
   * a read of what core's member endpoints produced — the roster changes the
   * moment one of them lands, including when another admin makes the change.
   *
   * Also the source for `getMemberDisplayName`: the ledger stores uids, and
   * rules let a user read only their own `users/{uid}` doc, so co-member names
   * can only come from the identity snapshot on the membership document.
   *
   * Unordered, like core's `list_members` — Firestore's default is by document
   * id (the uid), and an `orderBy("joined_at")` would silently hide any
   * membership written before that field existed.
   */
  subscribeToMembers: (orgId: string) => {
    if (!orgId) return () => {};

    const existing = get()._membersSubscription;
    if (existing?.key === orgId) return existing.stop;
    existing?.stop();

    set({ members: [], isLoadingMembers: true, membersError: null });

    const unsubscribe = onSnapshot(
      collection(db, `organizations/${orgId}/members`),
      (snapshot) => {
        const members = snapshot.docs.map(toMember);
        // The caller's own row is in this snapshot, so re-role them live rather
        // than leaving `orgRole` frozen at whatever `/organizations/me` returned
        // on page load: an admin promoting a viewer to editor would otherwise
        // leave that member staring at a read-only UI until they reloaded.
        // Fall back to the current value if their row is momentarily absent —
        // losing the role would hide controls they legitimately have.
        const uid = get().currentUser?.uid;
        set({
          members,
          orgRole:
            members.find((member) => member.uid === uid)?.role ?? get().orgRole,
          isLoadingMembers: false,
          membersError: null,
        });
      },
      (error) => {
        console.error("Error in members onSnapshot:", error);
        set({
          isLoadingMembers: false,
          membersError: error.message || "Failed to load members",
        });
      },
    );

    const stop = () => {
      unsubscribe();
      if (get()._membersSubscription?.key === orgId) {
        set({ _membersSubscription: null });
      }
    };
    set({ _membersSubscription: { key: orgId, stop } });
    return stop;
  },

  /**
   * Live org activity feed, newest first. `targetPath` narrows it to one
   * document's history — "Alice created it, Bob edited it".
   *
   * Read straight from Firestore rather than through core's `/actions`
   * endpoint: an entry has to show up the moment it lands, including the ones
   * core writes (adding a member goes through it), and a one-shot fetch only
   * ever showed what existed when the feed mounted. `firestore.rules` lets
   * every member read the collection and both orderings are indexed.
   *
   * ponytail: one feed at a time — the org page mounts a single ActivityFeed.
   * Two mounted at once with different `targetPath`s would take turns
   * replacing each other's `actions`; key the state by feed if that happens.
   */
  subscribeToActions: (orgId, { targetPath, limit = 50 } = {}) => {
    if (!orgId) return () => {};

    const key = `${orgId}|${targetPath ?? ""}|${limit}`;
    const existing = get()._actionsSubscription;
    if (existing?.key === key) return existing.stop;
    existing?.stop();

    set({ actions: [], isLoadingActions: true, actionsError: null });

    const entries = collection(db, `organizations/${orgId}/user_actions_ledger`);
    const constraints = [orderBy("at", "desc"), fbLimit(limit)];
    const unsubscribe = onSnapshot(
      targetPath
        ? query(entries, where("target_path", "==", targetPath), ...constraints)
        : query(entries, ...constraints),
      (snapshot) => {
        set({
          actions: snapshot.docs.map(toActionEntry),
          isLoadingActions: false,
          actionsError: null,
        });
      },
      (error) => {
        console.error("Error in user_actions_ledger onSnapshot:", error);
        set({
          isLoadingActions: false,
          actionsError: error.message || "Failed to load activity",
        });
      },
    );

    // Clearing the record on stop is what lets a remount re-listen instead of
    // being handed back an already-cancelled subscription.
    const stop = () => {
      unsubscribe();
      if (get()._actionsSubscription?.key === key) {
        set({ _actionsSubscription: null });
      }
    };
    set({ _actionsSubscription: { key, stop } });
    return stop;
  },

  getMemberDisplayName: (uid: string) =>
    get().members.find((member) => member.uid === uid)?.display_name ??
    "another member",
});
